import { useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { Plus, Briefcase, LayoutGrid, List } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { ConfirmDialog } from '@/shared/components/ui/ConfirmDialog';
import { useUser } from '@/shared/hooks/useAuth';
import { useDeals, useDealScores, useDealMutations } from '../hooks/useDeals';
import { useUserOptions } from '@/shared/hooks/useUserOptions';
import { PipelineSummary } from '../components/PipelineSummary';
import { DealsKanban } from '../components/DealsKanban';
import { DealsTable } from '../components/DealsTable';
import { DealFormModal } from '../components/DealFormModal';
import { DealDrawer } from '../components/DealDrawer';
import { STAGE_OPTIONS } from '../constants';
import type {
  CreateDealPayload,
  Deal,
  DealStage,
  DealsFilters,
  UpdateDealPayload,
} from '../types';

type ViewMode = 'kanban' | 'table';

export function DealsPage() {
  const user = useUser();
  const role = user?.role ?? '';
  const isPrivileged = ['admin', 'superadmin'].includes(role); // can filter by assignee
  const canDelete = isPrivileged; // backend: DELETE is admin/superadmin only

  // ─── View + filters ─────────────────────────────────────────────────────────
  const [view, setView] = useState<ViewMode>('kanban'); // pipeline first
  const [search, setSearch] = useState('');
  const [stage, setStage] = useState('');
  const [assignedTo, setAssignedTo] = useState('');

  const filters = useMemo<DealsFilters>(() => {
    const f: DealsFilters = {};
    if (stage) f.stage = stage;
    if (assignedTo) f.assignedTo = assignedTo;
    return f;
  }, [stage, assignedTo]);

  // ─── Data ─────────────────────────────────────────────────────────────────
  const { data, isLoading, isError } = useDeals(filters);
  const { data: users = [] } = useUserOptions();
  const { data: scoreMap = {} } = useDealScores();
  const { createDeal, updateDeal, deleteDeal } = useDealMutations();

  // ─── UI state ───────────────────────────────────────────────────────────────
  const [formOpen, setFormOpen] = useState(false);
  const [editDeal, setEditDeal] = useState<Deal | null>(null);
  const [drawerDeal, setDrawerDeal] = useState<Deal | null>(null);
  const [confirmDeal, setConfirmDeal] = useState<Deal | null>(null);

  // Merge AI scores (only for deals already visible to this user) + client search
  const deals = useMemo<Deal[]>(() => {
    const list = (data?.deals ?? []).map((d) => ({
      ...d,
      ai_score: scoreMap[d.id] ?? d.ai_score,
    }));
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((d) =>
      [d.title, d.business_name, d.contact_name, d.assigned_name].some((f) =>
        (f ?? '').toLowerCase().includes(q),
      ),
    );
  }, [data, scoreMap, search]);

  // ─── Handlers ───────────────────────────────────────────────────────────────
  const handleCreate = () => {
    setEditDeal(null);
    setFormOpen(true);
  };

  const handleEdit = (deal: Deal) => {
    setDrawerDeal(null);
    setEditDeal(deal);
    setFormOpen(true);
  };

  const handleSubmit = (payload: CreateDealPayload | UpdateDealPayload) => {
    if (editDeal) {
      updateDeal.mutate(
        { id: editDeal.id, payload: payload as UpdateDealPayload },
        {
          onSuccess: () => {
            toast.success('Deal updated.');
            setFormOpen(false);
            setEditDeal(null);
          },
          onError: () => toast.error('Failed to update deal.'),
        },
      );
    } else {
      createDeal.mutate(payload as CreateDealPayload, {
        onSuccess: () => {
          toast.success('Deal created.');
          setFormOpen(false);
        },
        onError: () => toast.error('Failed to create deal.'),
      });
    }
  };

  const handleStageChange = (deal: Deal, nextStage: DealStage) => {
    updateDeal.mutate(
      { id: deal.id, payload: { stage: nextStage } },
      {
        onSuccess: () => toast.success(`Moved to ${nextStage.replace('_', ' ')}.`),
        onError: () => toast.error('Failed to move deal.'),
      },
    );
  };

  const handleDelete = () => {
    if (!confirmDeal) return;
    deleteDeal.mutate(confirmDeal.id, {
      onSuccess: () => {
        toast.success('Deal deleted.');
        setConfirmDeal(null);
        setDrawerDeal(null);
      },
      onError: () => toast.error('Failed to delete deal.'),
    });
  };

  const assigneeFilterOptions = [
    { value: '', label: 'All owners' },
    ...users.map((u) => ({ value: u.id, label: u.display_name })),
  ];

  return (
    <div className="flex h-full flex-col gap-4 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Briefcase className="h-6 w-6 text-primary" />
          <div>
            <h1 className="text-display-sm font-semibold text-on-surface">Deals</h1>
            <p className="text-body-sm text-on-surface-variant">
              {deals.length} deal{deals.length !== 1 ? 's' : ''} in pipeline
            </p>
          </div>
        </div>
        <Button onClick={handleCreate} size="md">
          <Plus className="h-4 w-4" />
          New Deal
        </Button>
      </div>

      {/* Filters + view toggle */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="w-56">
          <Input
            name="deals-search"
            placeholder="Search deals…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="w-44">
          <Select
            name="deals-stage-filter"
            value={stage}
            onChange={(e) => setStage(e.target.value)}
            options={[{ value: '', label: 'All stages' }, ...STAGE_OPTIONS]}
          />
        </div>
        {isPrivileged && (
          <div className="w-48">
            <Select
              name="deals-owner-filter"
              value={assignedTo}
              onChange={(e) => setAssignedTo(e.target.value)}
              options={assigneeFilterOptions}
            />
          </div>
        )}
        <div className="ml-auto flex items-center gap-1 rounded-DEFAULT border border-outline p-0.5">
          <Button
            variant={view === 'kanban' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setView('kanban')}
          >
            <LayoutGrid className="h-4 w-4" /> Pipeline
          </Button>
          <Button
            variant={view === 'table' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setView('table')}
          >
            <List className="h-4 w-4" /> Table
          </Button>
        </div>
      </div>

      {/* Summary (currency-grouped) */}
      {!isLoading && !isError && <PipelineSummary deals={deals} />}

      {/* Body */}
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : isError ? (
        <EmptyState title="Failed to load deals" description="Check your connection or try refreshing." />
      ) : deals.length === 0 ? (
        <EmptyState
          title={search || stage || assignedTo ? 'No matching deals' : 'No deals yet'}
          description={
            search || stage || assignedTo
              ? 'Try adjusting your filters.'
              : 'Create your first deal to start building the pipeline.'
          }
          action={!search && !stage && !assignedTo ? <Button onClick={handleCreate}>New Deal</Button> : undefined}
        />
      ) : view === 'kanban' ? (
        <DealsKanban deals={deals} onOpen={setDrawerDeal} onStageChange={handleStageChange} />
      ) : (
        <div className="flex-1 overflow-y-auto">
          <DealsTable deals={deals} onOpen={setDrawerDeal} />
        </div>
      )}

      {/* Create / Edit */}
      <DealFormModal
        open={formOpen}
        deal={editDeal}
        users={users}
        loading={createDeal.isPending || updateDeal.isPending}
        onClose={() => {
          setFormOpen(false);
          setEditDeal(null);
        }}
        onSubmit={handleSubmit}
      />

      {/* Detail */}
      <DealDrawer
        deal={drawerDeal}
        canDelete={canDelete}
        deleting={deleteDeal.isPending}
        onClose={() => setDrawerDeal(null)}
        onEdit={handleEdit}
        onDelete={setConfirmDeal}
      />

      {/* Delete confirm */}
      <ConfirmDialog
        open={!!confirmDeal}
        title="Delete deal"
        message={`Delete "${confirmDeal?.title ?? ''}"? This cannot be undone.`}
        confirmLabel="Delete"
        danger
        loading={deleteDeal.isPending}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDeal(null)}
      />
    </div>
  );
}
